import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useConfig } from "../context/ConfigContext";
import MarkDoneDialog from "../components/MarkDoneDialog";
import { toast } from "sonner";
import {
  Wand2, CheckCircle2, Clock, ArrowRight, Users, ImagePlus, FileText,
  Film, Mail, MessageSquare, Video, Sparkles, Plane, CalendarRange,
} from "lucide-react";

const QUICK = [
  { label: "What should I do today?", icon: Sparkles, type: "auto", testid: "quick-today" },
  { label: "Create Post", icon: FileText, type: "post", testid: "quick-post" },
  { label: "Create Image", icon: ImagePlus, type: "image", testid: "quick-image" },
  { label: "Create Story", icon: Film, type: "story", testid: "quick-story" },
  { label: "Create Reel", icon: Video, type: "reel", testid: "quick-reel" },
  { label: "Create Email", icon: Mail, type: "email", testid: "quick-email" },
  { label: "Create Text", icon: MessageSquare, type: "text", testid: "quick-text" },
];

const fmtType = { post: "Facebook post", story: "Instagram Story", reel: "Reel", email: "Email", text: "Text blast", image: "Instagram post", followup: "Follow-up" };

export default function Today() {
  const { config } = useConfig();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [markRec, setMarkRec] = useState(null);
  const [onboarded, setOnboarded] = useState(true);

  const load = () => api.today().then(setData).catch(() => {});
  useEffect(() => {
    load();
    api.onboardingStatus().then((s) => setOnboarded(s.done)).catch(() => {});
  }, []);

  if (!data) return <div className="text-[#94A3B8] font-mono2 text-sm">Loading your marketing direction…</div>;
  const rec = data.recommendation;

  const goCreate = (spec, auto) => nav("/create", { state: { spec, auto } });

  const confirmDone = async (payload) => {
    const r = markRec;
    setMarkRec(null);
    await api.completeRec({
      action_type: fmtType[r.format] || "Facebook post",
      platform: r.channel, audience: r.audience, pillar: r.pillar,
      theme: r.theme, cta: r.cta, notes: payload.notes, link: payload.link,
    });
    toast.success("Logged. Tomorrow's plan will adjust.");
    load();
  };

  return (
    <div className="space-y-6">
      {!onboarded && (
        <div className="card-lux p-4 flex items-center justify-between gap-4 flex-wrap" data-testid="onboarding-banner">
          <div className="text-sm text-[#94A3B8]">
            <span className="text-[#F3E5AB] font-semibold">First time?</span> Let's record what you've already done so recommendations start from your real campaign state.
          </div>
          <button data-testid="go-onboarding" onClick={() => nav("/onboarding")} className="gold-btn px-4 py-2 rounded-lg text-sm">Set up what we've done</button>
        </div>
      )}

      {/* Greeting + priority */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card-lux p-6 lg:p-8 fade-up">
          <div className="eyebrow text-[#D4AF37]">{data.greeting}</div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-2 text-[#F8FAFC] leading-tight">
            {rec.priority}
          </h1>
          <div className="mt-4 p-4 rounded-lg bg-[#12151E] border border-[#2A3042]">
            <div className="eyebrow text-[#D4AF37] mb-1">Why this matters now</div>
            <p className="text-[#94A3B8] leading-relaxed">{rec.priority_why}</p>
          </div>
          <div className="mt-4 flex items-center gap-3 text-sm text-[#64748B]">
            <span className="px-2.5 py-1 rounded-md bg-[#222736] text-[#94A3B8] font-mono2 text-xs">PHASE {rec.phase.key} · {rec.phase.name}</span>
            <span>{rec.phase.plain}</span>
          </div>
        </div>

        <div className="card-lux p-6 flex flex-col justify-between fade-up">
          <div>
            <div className="flex items-center gap-2 eyebrow text-[#D4AF37]"><Plane className="w-4 h-4" /> Days until Thailand</div>
            <div className="font-display text-6xl text-[#F3E5AB] mt-2" data-testid="days-until">{rec.days_until}</div>
            <div className="text-[#94A3B8] text-sm">days until departure · June 19, 2027</div>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-6">
            <Metric label="Done / wk" value={data.metrics.completed_this_week} />
            <Metric label="Follow-ups" value={data.metrics.followups_due} warn={data.metrics.followups_due > 0} />
            <Metric label="Deposited" value={data.metrics.deposited_travelers} />
          </div>
        </div>
      </div>

      {/* Quick actions moved below to keep Today's priority first on mobile */}

      {/* Must do */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <CheckCircle2 className="w-5 h-5 text-[#D4AF37]" />
          <h2 className="text-lg font-semibold">Must do today</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {rec.must_do.map((m, i) => (
            <RecCard key={m.id} rec={m} index={i}
              onCreate={() => (m.kind === "followup" ? nav("/leads") : goCreate(m.create_spec, false))}
              onDone={() => setMarkRec(m)} />
          ))}
        </div>
      </div>

      {/* If you have time */}
      {rec.if_time?.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Clock className="w-5 h-5 text-[#94A3B8]" />
            <h2 className="text-lg font-semibold text-[#94A3B8]">If you have time</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {rec.if_time.map((m) => (
              <RecCard key={m.id} rec={m} subtle
                onCreate={() => goCreate(m.create_spec, false)} onDone={() => setMarkRec(m)} />
            ))}
          </div>
        </div>
      )}

      {/* Follow-ups due */}
      {data.metrics.followups_due > 0 && (
        <div className="card-lux p-5 flex items-center justify-between gap-3 flex-wrap border-[#F59E0B]/30" data-testid="followups-due-section">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-[#F59E0B]" />
            <span className="text-[#F8FAFC]">
              <span className="font-semibold text-[#F59E0B]">{data.metrics.followups_due}</span> follow-up{data.metrics.followups_due !== 1 ? "s" : ""} due
            </span>
          </div>
          <button data-testid="open-followups" onClick={() => nav("/leads")} className="gold-btn px-4 py-2 rounded-lg text-sm">Open Follow-Up Director</button>
        </div>
      )}

      {/* Quick create actions */}
      <div>
        <div className="eyebrow text-[#94A3B8] mb-3">Quick create</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
          {QUICK.map((q) => (
            <button
              key={q.label} data-testid={q.testid}
              onClick={() => (q.type === "auto" ? goCreate(null, true) : goCreate({ content_type: q.type }, false))}
              className="card-lux p-4 flex flex-col items-center gap-2 text-center hover:-translate-y-0.5 transition-transform min-h-[84px] justify-center"
            >
              <q.icon className="w-5 h-5 text-[#D4AF37]" />
              <span className="text-xs text-[#94A3B8] leading-tight">{q.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* This week + recent */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card-lux p-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2"><CalendarRange className="w-5 h-5 text-[#D4AF37]" /><h2 className="font-semibold">This week</h2></div>
            <button onClick={() => nav("/weekly")} className="text-xs text-[#94A3B8] hover:text-[#F3E5AB] inline-flex items-center gap-1">Full plan <ArrowRight className="w-3 h-3" /></button>
          </div>
          <div className="text-sm text-[#94A3B8] mb-3">{data.week.objective} · {data.week.recommended_frequency}</div>
          <div className="space-y-1.5">
            {data.week.plan.slice(0, 5).map((d) => (
              <div key={d.day} className="flex items-center justify-between text-sm py-1.5 border-b border-[#2A3042]/60">
                <span className="text-[#64748B] w-24">{d.day}</span>
                <span className="text-[#F8FAFC] flex-1">{d.pillar_label} · {d.theme}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="card-lux p-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold">Recent content</h2>
            <button onClick={() => nav("/content")} className="text-xs text-[#94A3B8] hover:text-[#F3E5AB] inline-flex items-center gap-1">Library <ArrowRight className="w-3 h-3" /></button>
          </div>
          {data.recent_content?.length ? (
            <div className="space-y-2">
              {data.recent_content.map((c) => (
                <div key={c.id} className="flex items-center justify-between text-sm py-1.5 border-b border-[#2A3042]/60">
                  <span className="text-[#F8FAFC] truncate flex-1">{c.title}</span>
                  <span className="text-xs px-2 py-0.5 rounded bg-[#222736] text-[#94A3B8] ml-2">{c.status}</span>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-[#64748B]">Nothing yet — create your first piece from the buttons above.</p>}
        </div>
      </div>

      <MarkDoneDialog open={!!markRec} onOpenChange={(o) => !o && setMarkRec(null)} onConfirm={confirmDone}
        title={markRec ? `Mark done: ${markRec.title}` : "Mark done"} />
    </div>
  );
}

function Metric({ label, value, warn }) {
  return (
    <div className="text-center p-2 rounded-lg bg-[#12151E] border border-[#2A3042]">
      <div className={`font-display text-2xl ${warn ? "text-[#F59E0B]" : "text-[#F3E5AB]"}`}>{value}</div>
      <div className="text-[10px] text-[#64748B] uppercase tracking-wider">{label}</div>
    </div>
  );
}

function RecCard({ rec, index, onCreate, onDone, subtle }) {
  const Icon = rec.kind === "followup" ? Users : Wand2;
  return (
    <div className={`card-lux p-5 flex flex-col ${subtle ? "opacity-90" : ""}`} data-testid={`rec-card-${index ?? "opt"}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="w-6 h-6 rounded-full bg-[#222736] flex items-center justify-center text-xs font-mono2 text-[#D4AF37]">{(index ?? 0) + 1}</span>
        <span className="eyebrow text-[#94A3B8]">{rec.channel} · {rec.pillar_label}</span>
      </div>
      <h3 className="font-display text-xl text-[#F8FAFC] leading-tight mb-1">{rec.title}</h3>
      <p className="text-sm text-[#94A3B8] flex-1">{rec.why}</p>
      <div className="text-xs text-[#64748B] mt-3 mb-3">To: {rec.audience} · CTA: {rec.cta}</div>
      <div className="flex gap-2">
        <button data-testid={`create-it-${index ?? "opt"}`} onClick={onCreate} className="gold-btn flex-1 py-2 rounded-lg text-sm inline-flex items-center justify-center gap-1.5">
          <Icon className="w-4 h-4" /> {rec.kind === "followup" ? "Open follow-ups" : "Create it for me"}
        </button>
        <button data-testid={`mark-done-${index ?? "opt"}`} onClick={onDone} className="px-3 py-2 rounded-lg border border-[#2A3042] text-[#94A3B8] hover:text-[#10B981] hover:border-[#10B981]/40 text-sm">Done</button>
      </div>
    </div>
  );
}
